import { useState } from "react";

import { Link, useRevalidator } from "@remix-run/react";
import { format } from "date-fns";
import { Calendar, Check, History, Link2, Unlink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { useApiClient } from "~/hooks/use-api-client";

import type { ConfidenceLevel } from "../../parts-similarity/utils/confidence";
import { AutoLinkReviewDialog } from "./auto-link-review-dialog";

// AIDEV-NOTE: Component to show part interaction history and allow unlinking

// AIDEV-NOTE: Prefetched history data from session loader to eliminate N+1 API calls
interface PrefetchedHistoryData {
    totalInteractions: number;
    nthTime: number | null;
    interactionNames: Array<{ name: string; isCurrentSession: boolean }>;
}

interface PartInteractionHistoryPopoverProps {
    partId: string;
    currentSessionId: string;
    currentInteractionId: string;
    /** The canonical part name (part.name) for aka display logic */
    partName?: string;
    /** The name of this interaction in the current session */
    currentInteractionName?: string;
    /** Number of times this part has been seen (if known from parent) */
    interactionCount?: number;
    /** Whether this part was auto-linked (shows badge) */
    isAutoLinked?: boolean;
    /** Current part interaction details for the auto-link review dialog */
    currentPartDetails?: {
        description?: string | null;
        recap?: string | null;
        quote?: string | null;
        attributes?: Array<{ name: string; content: string }> | null;
    };
    /** Similarity data for the auto-link review dialog */
    similarityData?: {
        partId: string;
        partName?: string;
        partDescription?: string | null;
        confidence?: ConfidenceLevel | number;
        reason?: string;
        reasoning?: string;
        differences?: string | null;
    };
    /** Prefetched history metadata from session loader - eliminates N+1 API calls */
    prefetchedHistory?: PrefetchedHistoryData;
    /** If true, hide interactive elements (for public session access) */
    isPublicSession?: boolean;
}

interface InteractionHistoryItem {
    interactionId: string;
    name: string;
    description: string;
    recap?: string | null;
    quote?: string | null;
    attributes?: Array<{ name: string; content: string }> | null;
    sessionId: string;
    sessionTitle: string;
    sessionDate: string;
    isCurrentSession: boolean;
}

interface InteractionHistoryResponse {
    partId: string;
    totalInteractions: number;
    nthTime: number | null;
    history: InteractionHistoryItem[];
}

function getOrdinalSuffix(n: number): string {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/**
 * Get the "aka" name to display for a linked part.
 * Logic:
 * 1. If part.name differs from ALL interaction names in history, show part.name
 * 2. Otherwise, find any previous interaction with a name !== current interaction name
 * 3. If all names are the same, return null (don't show "also known as")
 *
 * @param partName - The canonical part name (part.name)
 * @param currentInteractionName - The name of the interaction in the current session
 * @param history - Array of interaction history items
 * @returns The aka name to display, or null if no aka should be shown
 */
export function getAkaName(
    partName: string | undefined,
    currentInteractionName: string | undefined,
    history: InteractionHistoryItem[] | undefined,
): string | null {
    if (!history || history.length === 0) {
        return null;
    }

    // Get all interaction names from history
    const allInteractionNames = history.map((item) => item.name);

    // Check if part.name differs from ALL interaction names
    if (partName && !allInteractionNames.includes(partName)) {
        return partName;
    }

    // Find any previous interaction with a different name than current
    // This searches all history to find a name worth showing as "also known as"
    const differentNameInteraction = history.find(
        (item) =>
            !item.isCurrentSession && item.name !== currentInteractionName,
    );

    if (differentNameInteraction) {
        return differentNameInteraction.name;
    }

    return null;
}

/**
 * Get the "aka" name from prefetched interaction names.
 * Same logic as getAkaName but works with the simpler prefetched data structure.
 */
function getAkaNameFromPrefetched(
    partName: string | undefined,
    currentInteractionName: string | undefined,
    interactionNames:
        | Array<{ name: string; isCurrentSession: boolean }>
        | undefined,
): string | null {
    if (!interactionNames || interactionNames.length === 0) {
        return null;
    }

    // Get all interaction names
    const allNames = interactionNames.map((item) => item.name);

    // Check if part.name differs from ALL interaction names
    if (partName && !allNames.includes(partName)) {
        return partName;
    }

    // Find any previous interaction with a different name than current
    const differentNameInteraction = interactionNames.find(
        (item) =>
            !item.isCurrentSession && item.name !== currentInteractionName,
    );

    if (differentNameInteraction) {
        return differentNameInteraction.name;
    }

    return null;
}

export function PartInteractionHistoryPopover({
    partId,
    currentSessionId,
    currentInteractionId,
    partName,
    currentInteractionName,
    interactionCount,
    isAutoLinked,
    currentPartDetails,
    similarityData,
    prefetchedHistory,
    isPublicSession = false,
}: PartInteractionHistoryPopoverProps) {
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [isUnlinking, setIsUnlinking] = useState(false);
    const [isConfirming, setIsConfirming] = useState(false);
    const [shouldFetchFullHistory, setShouldFetchFullHistory] = useState(false);
    const apiClient = useApiClient();
    const revalidator = useRevalidator();

    // AIDEV-NOTE: Lazy-load full interaction history only when dialog opens
    // Prefetched data is used for immediate display (count, nthTime, akaName)
    // Full history (with descriptions, dates, etc.) is only needed for the dialog
    const { data, isLoading } = useQuery<InteractionHistoryResponse>({
        queryKey: ["part-interaction-history", partId, currentSessionId],
        queryFn: async () => {
            const response = await apiClient.get("/parts/interaction-history", {
                params: { partId, currentSessionId },
            });
            if (!response.ok) {
                throw new Error("Failed to fetch history");
            }
            return response.json();
        },
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
        enabled: shouldFetchFullHistory, // Only fetch when dialog is opened
    });

    const handleUnlink = async () => {
        setIsUnlinking(true);
        try {
            const response = await apiClient.post("/parts/unlink-interaction", {
                partInteractionId: currentInteractionId,
            });

            if (response.ok) {
                // AIDEV-NOTE: Submit feedback for bad auto-link to collect production data
                await apiClient.post("/feedback", {
                    sessionId: currentSessionId,
                    partId: currentInteractionId,
                    content: "",
                    score: -1,
                    metadata: {
                        type: "similarity_auto_link",
                        partId,
                        partInteractionId: currentInteractionId,
                    },
                });

                toast.success("Part unlinked from previous sessions");
                setIsConfirmDialogOpen(false);
                revalidator.revalidate();
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || "Failed to unlink part");
            }
        } catch (err) {
            toast.error("Failed to unlink part");
        } finally {
            setIsUnlinking(false);
        }
    };

    const handleConfirmLink = async () => {
        setIsConfirming(true);
        try {
            const response = await apiClient.post("/parts/confirm-auto-link", {
                partInteractionIds: [currentInteractionId],
            });

            if (response.ok) {
                toast.success("Link confirmed");
                setIsConfirmDialogOpen(false);
                revalidator.revalidate();
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || "Failed to confirm link");
            }
        } catch (err) {
            toast.error("Failed to confirm link");
        } finally {
            setIsConfirming(false);
        }
    };

    // AIDEV-NOTE: Use prefetched data for immediate display, fall back to fetched data
    const totalCount =
        prefetchedHistory?.totalInteractions ??
        data?.totalInteractions ??
        interactionCount ??
        0;
    const nthTime = prefetchedHistory?.nthTime ?? data?.nthTime;

    // Compute aka name from prefetched data or fetched history
    const akaName = prefetchedHistory
        ? getAkaNameFromPrefetched(
              partName,
              currentInteractionName,
              prefetchedHistory.interactionNames,
          )
        : getAkaName(partName, currentInteractionName, data?.history);

    // Handler for opening the dialog - triggers lazy fetch of full history
    const handleOpenDialog = () => {
        setShouldFetchFullHistory(true);
        setIsConfirmDialogOpen(true);
    };

    // Don't show if part has only been seen once (unless dialog is already open)
    if (totalCount <= 1 && !isConfirmDialogOpen) {
        return null;
    }

    // AIDEV-NOTE: Compute display text using prefetched akaName
    // Always show "also known as" when there's an alternate name, regardless of auto-link status
    // Never show "(X sessions total)" suffix - just the ordinal form
    const displayText = akaName
        ? `also known as ${akaName}`
        : nthTime
          ? `${getOrdinalSuffix(nthTime)} time you're meeting this part`
          : `Seen ${totalCount} times`;

    return (
        <>
            <div className="mt-1 flex items-center gap-1.5 text-sm">
                {/* AIDEV-NOTE: Display uses prefetched data, dialog triggers lazy fetch */}
                <Link
                    to={`/parts/${partId}`}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                >
                    <History className="size-3.5" />
                    <span>{displayText}</span>
                </Link>
                {/* AIDEV-NOTE: Hidden on mobile - similarity modal not optimized for small screens */}
                {/* AIDEV-NOTE: Hide Review badge for public sessions */}
                {isAutoLinked && !isPublicSession && (
                    <Badge
                        variant="outline"
                        className="hidden md:inline-flex text-xs py-0 px-1.5 cursor-pointer bg-secondary border-primary/30 text-primary hover:bg-secondary/80"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOpenDialog();
                        }}
                    >
                        Review
                    </Badge>
                )}
            </div>

            {/* AIDEV-NOTE: Use AutoLinkReviewDialog for enhanced left/right split view when data is available */}
            {isAutoLinked && currentPartDetails ? (
                <AutoLinkReviewDialog
                    isOpen={isConfirmDialogOpen}
                    onOpenChange={setIsConfirmDialogOpen}
                    currentPart={{
                        name: currentInteractionName || "Unknown Part",
                        description: currentPartDetails.description,
                        recap: currentPartDetails.recap,
                        quote: currentPartDetails.quote,
                        attributes: currentPartDetails.attributes,
                    }}
                    linkedPart={
                        data?.history
                            ? {
                                  partId,
                                  partName:
                                      similarityData?.partName ||
                                      partName ||
                                      "Linked Part",
                                  partDescription:
                                      similarityData?.partDescription,
                                  confidence: similarityData?.confidence,
                                  reasoning:
                                      similarityData?.reason ||
                                      similarityData?.reasoning,
                                  differences: similarityData?.differences,
                                  history: data.history,
                              }
                            : null
                    }
                    onConfirm={handleConfirmLink}
                    onUnlink={handleUnlink}
                    isConfirming={isConfirming}
                    isUnlinking={isUnlinking}
                />
            ) : (
                /* Fallback to simple confirm dialog when no currentPartDetails */
                <Dialog
                    open={isConfirmDialogOpen}
                    onOpenChange={setIsConfirmDialogOpen}
                    analyticsId="confirm-part-link-dialog"
                >
                    <DialogContent onClick={(e) => e.stopPropagation()}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Link2 className="size-5" />
                                Confirm Link
                            </DialogTitle>
                            <DialogDescription>
                                This part was automatically linked to previous
                                sessions. Review the history below and confirm
                                if this is correct.
                            </DialogDescription>
                        </DialogHeader>

                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <span className="text-muted-foreground">
                                    Loading history...
                                </span>
                            </div>
                        ) : (
                            <div className="max-h-64 space-y-2 overflow-y-auto">
                                {data?.history.map((item) => (
                                    <div
                                        key={item.interactionId}
                                        className={`rounded-md border p-2 text-sm ${
                                            item.isCurrentSession
                                                ? "border-primary/30 bg-primary/5"
                                                : "border-border"
                                        }`}
                                    >
                                        <div className="font-medium">
                                            {item.name}
                                            {item.isCurrentSession && (
                                                <span className="ml-2 text-xs text-primary">
                                                    (this session)
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                            <Calendar className="size-3" />
                                            {format(
                                                new Date(item.sessionDate),
                                                "MMM d, yyyy",
                                            )}
                                            {" · "}
                                            {item.sessionTitle}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                className="gap-2"
                                onClick={handleUnlink}
                                disabled={
                                    isUnlinking || isConfirming || isLoading
                                }
                            >
                                <Unlink className="size-4" />
                                {isUnlinking
                                    ? "Unlinking..."
                                    : "Not the same part"}
                            </Button>
                            <Button
                                className="gap-2"
                                onClick={handleConfirmLink}
                                disabled={
                                    isUnlinking || isConfirming || isLoading
                                }
                            >
                                <Check className="size-4" />
                                {isConfirming
                                    ? "Confirming..."
                                    : "Confirm link"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}
